/* eslint-disable rxjs/no-unsafe-first */
/* eslint-disable rxjs/no-unsafe-catch */
import { inject, Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import {
    catchError,
    concatMap,
    distinctUntilChanged,
    filter,
    first,
    map,
    mergeMap,
    switchMap,
    tap,
} from 'rxjs/operators';

import { ToggleChat } from '../../../chat/store/chat.actions';
import { GA4ConfigurationEvents } from '../configs/ga4-configuration-events.class';
import { isValidModal, isValidSidebarComponent } from '../helpers/ga4-context.helper';
import { GA4EventConfiguration } from '../models/configuration.model';
import {
    Ga4ContentType,
    Ga4EventLocation,
    GA4ImActionType,
    Ga4ImTypeNotification,
} from '../models/event-data.model';
import { GA4Event, GA4EventType } from '../models/general.model';
import { GA4AnalyticsPublicActions } from '../public.api';
import { GA4EventParserService } from '../services/ga4-event-parser.service';
import { initBeforeUnloadListener, setPageReferrer } from '../services/ga4-page-referrer.service';
import { GA4ProductOriginHelpers } from '../services/ga4-product-origin.helpers';

import * as GA4AnalyticsActions from './ga4-analytics.actions';
import { Ga4SavePageReferrerSuccess, Ga4SetPageviewSent } from './ga4-analytics.actions';
import { getPageViewIsSent } from './ga4-analytics.selectors';

import { ApiSizeInfo } from '@api/_api/api-cart/api-cart.models';
import { CartItem } from '@api/cart/cart.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { PageSection } from '@core/domain/types/page-sections.types';
import { GridElemType } from '@core/domain/types/product.types';
import { SetPage } from '@core/infrastructure/state/core-commands.actions';
import { GetContextOk } from '@core/infrastructure/state/core-events.actions';
import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { getProducts } from '@features/cms-render/infrastructure/state/cms-render.selectors';
import { FeelSubscriptionFeaturePublicActions } from '@features/feel/public.api';
import { ProductPublicModels } from '@features/products/product';
import {
    SidebarSelectors,
    SidebarActionTypes,
    SidebarCustomComponents,
} from '@features/sidebar';
import { CloseSidebarCustomSuccessCommandProps } from '@features/sidebar/domain/models/sidebar.models';
import { WishlistPublicActions, WishlistPublicSelectors } from '@features/wishlist';
import { ItxObservabilityCommands } from '@frontend/libs/itx-observability-core';
import { OpenSearchFromHeader } from '@layout/infrastructure/state/layout.actions';
import { SetGridTypeCategoryGrid } from '@pages/category/modules/category-grid/store/category-grid.actions';
import { getGridType } from '@pages/category/store/grid-type/grid-type.selectors';
import { SendCheckoutShippingError } from '@pages/checkout/shipping/store/shipping/shipping.actions';
import { OrderReturnStep } from '@pages/my-account/sections/returns/models/returns.models';
import { SetReturnStep } from '@pages/my-account/sections/returns/store/order-return/order-return.actions';
import { getProductPageDetail } from '@pages/product';
import { SetColorOnSelectedProduct } from '@pages/product/store/product-page.actions';
import { SearchSelectors } from '@pages/search';
import { AnalyticsClickSourceID, EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ConfigurationService } from '@shared/analytics/COMMON/services/configuration.service';
import {
    BundleClickAnalytics,
    ChatMessageAnalytics,
    ClickOnCollectionStoryAnalytics,
    ClickProductoBuscadorAnalytics,
    StockErrorAnalytics,
    EventFormErrorAnalytics,
    ObservedElementClick,
    ProductClickAnalytics,
    RelatedProductClickAnalytics,
    SeleccionarTallaAnalytics,
    SubscribeComingSoonBackSoonAnalytics,
    SubscribeComingSoonBackSoonErrorAnalytics,
    SmartAssistantView,
} from '@shared/analytics/store/app-events-analytics.actions';
import { IS_OBSERVABLE_CONDITONS } from '@shared/directives/element-observer.directive';
import { NavigateToCategoryPageAnalytics } from '@shared/menu-categories/store/menu-categories.actions';
import { GoToSection } from '@shared/navigation';
import { LoadAsyncCheckoutError } from '@shared/purchase/store/purchase.actions';
import { AdjustItemSizeShopcart } from '@shared/shopcart/store/shopcart.actions';
import { CloseModalFromComponent, OpenModalFromComponent } from '@ui/dialog/store/modal.actions';
import { getActiveModalData } from '@ui/dialog/store/modal.selectors';

const NON_PAGEVIEW_MODALS_AND_SIDEBARS = [
    'app-account-details-ok-dialog',
    'app-feel-subscription-modal',
    'linked-account',
    'my-account-feel-magazine-modal-component',
    'ProductFilterComponent',
    'ProductStockSidebarComponent',
    'tyc-modal',
    SidebarCustomComponents.COORDINATED_PRODUCTS,
    SidebarCustomComponents.SMART_ASSISTANT,
];

// #region Class Effects

@Injectable()
export class GA4AnalyticsEffects implements OnInitEffects {
    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly ga4ConfigurationEvents: GA4ConfigurationEvents,
        private readonly ga4EventParserService: GA4EventParserService,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly configurationService: ConfigurationService,
        private readonly translateService: TranslateService,
        private readonly coreStorageService: CoreStorageService,
    ) { }

    public ngrxOnInitEffects(): Action {
        return GA4AnalyticsActions.InitRefererResetTracking();
    }

    private getGa4Configuration(action) {
        const ga4Configuration: GA4EventConfiguration = this.ga4ConfigurationEvents.getConfigurationCodeEvent(action.name);
        return {
            ...action,
            config: ga4Configuration,
        };
    }

    private getObservableBasedOnAction<T extends Action & { name: string }>(action: T): Observable<boolean> {
        let obs$: Observable<any> = of(true);
        if (action.name !== GA4EventType.page_view) {
            obs$ = this.store.select(getPageViewIsSent);
        }
        return obs$;
    }

    private prepareGa4Event(action): GA4Event {
        return this.ga4EventParserService.prepareGroupParams(
            action.config,
            action.eventFrom,
            action.data,
        );
    }

    public ga4Event$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4Event),
            concatMap((action) => this.configurationService.getIsGTMLoadedWithActualInfo$<typeof action>(action)),
            filter((action) => !!action),
            map((action) => this.getGa4Configuration(action)),
            mergeMap((action) => {
                return this.getObservableBasedOnAction<typeof action>(action).pipe(
                    filter(obs => !!obs),
                    first(),
                    map(() => this.prepareGa4Event(action)),
                    map((ga4Event) => GA4AnalyticsActions.Ga4SendDatalayer({
                        dataLayer: ga4Event,
                        eventFrom: action.eventFrom,
                    })),
                    catchError((error) => of(GA4AnalyticsActions.Ga4EventError({ error }))),
                );
            }),
        );
    });

    public Ga4EventError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4EventError),
            tap((action) => {
                console.error(action.error);
            }),
        );
    }, { dispatch: false });

    public ga4SendDatalayer$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4SendDatalayer),
            tap(action => {
                this.window.dataLayer.push(action.dataLayer);
            }),
            map((action) => {
                return GA4AnalyticsActions.LoadEventSuccess({
                    payload: {
                        type: 'ga4Event',
                        dataObject: action.dataLayer,
                        eventFrom: action.eventFrom,
                    },
                });
            }),
            catchError((error: string) => {
                console.error(error);
                return of(GA4AnalyticsActions.LoadEventError());
            }),
        );
    });

    // NOTE: Se comenta este efecto debido a que actualmente cuando un usuario acepta las cookies, se recarga la página
    // public pageViewOnLoadGTM$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(InitPrimaryLoadSuccess),
    //         switchMap(() => this.store.select(getIsGTMLoaded).pipe(distinctUntilChanged())),
    //         filter((isLoaded) => !!isLoaded),
    //         withLatestFrom(this.store.select(getPageViewIsSend)),
    //         filter(([_, isSend]) => !isSend),
    //         withLatestFrom(this.store.select(getPage)),
    //         map(([_, page]) => SetPage({ page }),
    //     );
    // });

    private readonly excludedPagesPageView: PageSection[] = [
        'ItxMyAccountPage',
    ];

    public pageViewAnalytics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            filter(({ page }) => !this.excludedPagesPageView.includes(page)),
            concatMap(({ page }) => {
                try {
                    return this.configurationService.pageDeferConditions(page).pipe(
                        map((data) => {
                            const eventFromSource = typeof data === 'object' ? page : data;

                            return GA4AnalyticsActions.Ga4Event({
                                name: GA4EventType.page_view,
                                eventFrom: EventFrom[eventFromSource],
                            });
                        }),
                    );
                } catch (error) {
                    ItxObservabilityCommands.sendObservabilityLog(error, 'GA4AnalyticsActions pageViewAnalytics');
                    return of(GA4AnalyticsActions.Ga4EventError({ error }));
                }
            }),
        );
    });

    public orderReturnAnalytics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetReturnStep),
            map((data: { step: OrderReturnStep }) => {
                let eventFrom: string;
                // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
                switch (data.step) {
                    case 0:
                        eventFrom = 'returnsOrderOptionsPage';
                        break;
                    case 1:
                        eventFrom = 'returnsOrderDeliveryPage';
                        break;
                    case 2:
                        eventFrom = 'returnsOrderListPage';
                        break;
                    case 4:
                        eventFrom = 'returnsOrderDetailPage';
                        break;
                    case 5:
                        eventFrom = 'returnsOrderConfirmationPage';
                        break;
                    default:
                        eventFrom = 'returnsOrderPage';
                        break;
                }

                return GA4AnalyticsActions.Ga4Event({ name: GA4EventType.page_view, eventFrom: EventFrom[eventFrom] });
            }),
        );
    });

    public EventSelectItemGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                ClickProductoBuscadorAnalytics,
                ProductClickAnalytics,
                RelatedProductClickAnalytics,
            ),
            concatLatestFrom(() => this.store.select(SearchSelectors.getSearchType)),
            map(([action, searchType]) => {
                const product = action?.product;
                const products = [product];
                let eventFrom: EventFrom = (action as any)?.eventFrom || (action as any)?.parentType;

                if (eventFrom === EventFrom.search && searchType) {
                    const eventFromMap = {
                        busqueda_relacionada: EventFrom.searchAdapterNextTerm,
                        busqueda_sugerida: EventFrom.searchAdapterAutocomplete,
                        manual: EventFrom.searchAdapter,
                        trending: EventFrom.searchAdapterTrend,
                        ultimas_busquedas: EventFrom.searchAdapterLastSearchs,
                    };

                    eventFrom = eventFromMap[searchType] || eventFrom;
                }

                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.select_item,
                    eventFrom,
                    data: {
                        baseProduct: (action as any)?.baseProduct,
                        category: (action as any)?.category,
                        items: products,
                        place: (action as any)?.place,
                        relatedsSectionId: (action as any)?.relatedsSectionId,
                        imBundleReference: (action as any)?.imBundleReference,
                    },
                });
            }),
        );
    });

    public EventSelectItemBundleGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BundleClickAnalytics),
            mergeMap((action) => {
                const product = action?.product;
                const baseProduct = action?.baseProduct || product;
                const place = action?.place;
                const parseEventFrom: EventFrom = action.eventFrom || action?.parentType as EventFrom;

                return product.bundleProducts.map((bundleProduct) => {
                    return GA4AnalyticsActions.Ga4Event({
                        name: GA4EventType.select_item,
                        eventFrom: parseEventFrom,
                        data: {
                            items: [bundleProduct],
                            imBundleReference: product.reference,
                            baseProduct,
                            place,
                        },
                    });
                });
            }),
        );
    });

    public ObservedElementClickToPromotion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ObservedElementClick),
            filter(({ observeType, link }) => observeType === 'promotion' && IS_OBSERVABLE_CONDITONS.isPromotion(link)),
            map(({ link, elementReference, $event }) => GA4AnalyticsActions.Ga4Event({
                name: GA4EventType.select_promotion,
                data: {
                    elementData: {
                        $event,
                        elementReference,
                        link,
                    },
                },
            })),
        );
    });

    public ObservedElementClickToVentaAnticipada$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ObservedElementClick),
            filter(({ link, observedData }) => observedData?.slug?.includes('mspot-web-notification')
                // Acción definida: Se accede a parrilla de venta anticipada || Se despliega menú de categorías
                && (link?.type === 'category' || link?.type === 'menu')),
            map((action) => {
                const element = this.window?.document?.querySelector(`[data-reference="${action?.elementReference}"]`);
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.select_content,
                    data: {
                        imLinkText: element?.textContent?.trim(),
                        contentType: Ga4ContentType.notification,
                        imActionType: GA4ImActionType.acceder_venta_anticipada,
                        imLinkType: 'texto',
                        imLocation: Ga4EventLocation.popup,
                        imType: Ga4ImTypeNotification.venta_anticipada,
                    },
                });
            }),
        );
    });

    public ObservedElementClickAccessLoyaltyFromLanding$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ObservedElementClick),
            filter(({ link, observedData }) => observedData?.slug === 'massimo-dutti-feel' && IS_OBSERVABLE_CONDITONS.isCmsLinkTracked(link, observedData?.slug)),
            map(({ elementReference, link }) => {
                const element = this.window.document.querySelector(`[data-reference="${elementReference}"]`);
                const imActionType = link?.section === 'showCRMConfig' ? GA4ImActionType.ir_a_fidelizacion : GA4ImActionType.unir_programa;

                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.select_content,
                    data: {
                        contentType: Ga4ContentType.loyalty,
                        imActionType,
                        imLinkText: element?.textContent?.trim(),
                    },
                });
            }),
        );
    });

    public feelCommonJoinGenerateLead$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FeelSubscriptionFeaturePublicActions.FeelSubscriptionJoinSuccess),
            map(() => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.generate_lead,
                    eventFrom: EventFrom.feelJoin,
                });
            }),
        );
    });

    public beginSearchEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenSearchFromHeader),
            map(({ from }) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.begin_search,
                    eventFrom: EventFrom[from],
                });
            }),
        );
    });

    public formErrorEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(EventFormErrorAnalytics),
            map(({ form, formName, eventFrom }) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.error_custom,
                    data: {
                        errorType: 'error_form',
                        formValues: form,
                        formName,
                    },
                    eventFrom,
                });
            }),
        );
    });

    public errorStockEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StockErrorAnalytics),
            map((eventData) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.error_custom,
                    data: {
                        errorType: 'error_servidor_unidadestock',
                        items: [eventData.item],
                    },
                    eventFrom: EventFrom.checkout,
                });
            }),
        );
    });

    public errorPaymentTypeEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadAsyncCheckoutError),
            map(({ selectedPayment }) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.error_custom,
                    data: {
                        errorType: 'error_payment_type',
                        errorData: { error: { description: selectedPayment.name } },
                    },
                    eventFrom: EventFrom.checkout,
                });
            }),
        );
    });

    public errorShippingTierEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SendCheckoutShippingError),
            map(({ selectedMethod }) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.error_custom,
                    data: {
                        errorType: 'error_shipping_tier',
                        errorData: { error: { description: selectedMethod.name } },
                    },
                    eventFrom: EventFrom.checkout,
                });
            }),
        );
    });

    public errorPageEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetPage),
            filter(({ page }) => page === 'Itx404ErrorPage' || page === 'Itx410ErrorPage'),
            map(({ page }) => {
                if (page === 'Itx410ErrorPage') return '410';
                return '404';
            }),
            map((errorPageId) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.error_custom,
                    data: {
                        errorType: 'error_page',
                        errorData: { error: { description: errorPageId } },
                    },
                    eventFrom: EventFrom.error,
                });
            }),
        );
    });

    public viewItemOnProductPage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4SendDatalayer),
            filter(({ dataLayer, eventFrom }) =>
                dataLayer.event === GA4EventType.page_view
                && dataLayer.context.page_title.includes('ficha_producto')
                && eventFrom === EventFrom.productdetail),
            concatLatestFrom(() => this.store.select(getProductPageDetail)),
            map(([_, product]) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.view_item,
                    eventFrom: EventFrom.productdetail,
                    data: {
                        items: [product],
                    },
                });
            }),
        );
    });

    public setGridTypeCategoryGridEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetGridTypeCategoryGrid),
            distinctUntilChanged(),
            filter((action) => !action.initGridType),
            map(() => {
                const gridType = this.store.selectSignal(getGridType)();
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.change_view_item_list,
                    eventFrom: EventFrom.parrilla,
                    data: {
                        category_view_type: gridType,
                    },
                });
            }),
        );
    });

    public openInteractionChatEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ToggleChat),
            map(() => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.interaction_chat,
                    eventFrom: EventFrom.chat,
                    data: {
                        imActionType: GA4ImActionType.abrir_chat,
                        imLinkType: 'icono',
                        imLinkText: '¿Necesitas ayuda?',
                    },
                });
            }),
        );
    });

    public firstMessageInteractionChatEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ChatMessageAnalytics),
            first((message: any) => message.from === 'user'),
            map(() => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.interaction_chat,
                    eventFrom: EventFrom.chat,
                    data: {
                        imActionType: GA4ImActionType.primer_mensaje_chat,
                    },
                });
            }),
        );
    });

    public SubscribeComingSoonBackSoonErrorEvent$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SubscribeComingSoonBackSoonErrorAnalytics),
            map((action) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.error_custom,
                    eventFrom: EventFrom.ProductComingSoonComponent,
                    data: {
                        errorData: action.error,
                        errorType: 'error_server_form',
                        formName: 'coming_soon',
                        imEventName: 'form_submit',
                        imLocation: Ga4EventLocation.popup,
                        imType: 'error_server_form',
                    },
                });
            }),
        );
    });

    public initBeforeUnloadListener$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.InitRefererResetTracking),
            tap(() => { initBeforeUnloadListener(this.window); }),
        );
    }, { dispatch: false });

    public savePageReferrer$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4SendDatalayer),
            filter(({ dataLayer }) => dataLayer.event === GA4EventType.page_view),
            tap(({ dataLayer }) => {
                setPageReferrer(dataLayer.context.page_location, this.window);
            }),
            map(() => Ga4SavePageReferrerSuccess()),
        );
    });

    public goToCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(NavigateToCategoryPageAnalytics),
            map(({ category }) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.select_content,
                    eventFrom: EventFrom.mainMenu,
                    data: {
                        menuCategory: category,
                        contentType: Ga4ContentType.catalog,
                        imActionType: GA4ImActionType.clickMenu,

                    },
                });
            }),
        );
    });

    public changeColorProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SetColorOnSelectedProduct),
            concatLatestFrom(() => [
                this.store.select(getProductPageDetail),
            ]),
            map(([_, currentProduct]) => {
                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.select_content,
                    eventFrom: undefined,
                    data: {
                        imLocation: Ga4EventLocation.fichaProducto,
                        contentType: Ga4ContentType.product_color,
                        imActionType: GA4ImActionType.seleccionar_color,
                        contentId: currentProduct?.reference,
                        items: [currentProduct],
                    },
                });
            }),
        ));

    public subscribeComingSoonBackSoon$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SubscribeComingSoonBackSoonAnalytics),
            concatLatestFrom(() => this.translateService.get('dev.product.notification.send')),
            map(([{ product }, linkText]) => {
                let imType: Ga4ImTypeNotification = undefined;

                if (product.status.selectedSize.visibilityValue === 'COMING_SOON') {
                    imType = Ga4ImTypeNotification.coming_soon;
                } else if (product.status.selectedSize.visibilityValue === 'BACK_SOON') {
                    imType = Ga4ImTypeNotification.back_soon;
                }

                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.select_content,
                    eventFrom: EventFrom['product-coming-soon-modal'],
                    data: {
                        contentId: product.reference,
                        contentType: Ga4ContentType.notification,
                        imActionType: GA4ImActionType.crear_notificacion,
                        imLinkText: linkText,
                        items: [product],
                        imType,
                    },
                });
            }),
        );
    });

    public LoadMiniCartModuleGA4$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SidebarActionTypes.OpenSidebar),
            concatLatestFrom(() => [
                this.translateService.get('header_shop_cart'), // REF-AN-4
                this.store.select(SidebarSelectors.viewCart),
            ]),
            filter(([_, __, hasViewCart]) => hasViewCart),
            map(([_, linktext]) => GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.select_content,
                eventFrom: undefined,
                data: {
                    contentType: Ga4ContentType.cart,
                    imLocation: Ga4EventLocation.header,
                    imActionType: GA4ImActionType.ver_minicesta,
                    imLinkText: linktext,
                },
            })),
        );
    });

    public trackGoToShopCartGA4$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GoToSection),
            filter(action => action.url === 'shopCart'),
            // REF-AN-3
            concatLatestFrom(() => this.translateService.get('dev.cart.goToShopcart')),
            map(([_, linktext]) => GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.select_content,
                eventFrom: undefined,
                data: {
                    contentType: Ga4ContentType.cart,
                    imLocation: Ga4EventLocation.sidebar,
                    imActionType: GA4ImActionType.ir_a_cesta,
                    imLinkText: linktext,
                },
            })),
        ));

    public eventBeginCartAnalitics$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GoToSection),
            filter(({ url }) => url === 'shopWish'),
            concatLatestFrom(() => this.translateService.get('dev.cart.wishlist')),
            map(([_, linktext]) => {
                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.select_content,
                    eventFrom: undefined,
                    data: {
                        contentType: Ga4ContentType.wishlist,
                        imLocation: Ga4EventLocation.wishlist,
                        imType: 'wishlist',
                        imActionType: GA4ImActionType.ver_wishlist,
                        imLinkText: linktext,
                    },
                });
            }),
        );
    });

    public openWishList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SidebarActionTypes.ShowMiniWish),
            // REF-AN-5
            concatLatestFrom(() => this.translateService.get('dev.cart.wishlist')),
            map(([_, linktext]) => {
                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.select_content,
                    eventFrom: undefined,
                    data: {
                        contentType: Ga4ContentType.wishlist,
                        imLocation: Ga4EventLocation.wishlist,
                        imType: 'wishlist',
                        imActionType: GA4ImActionType.ver_miniwishlist,
                        imLinkText: linktext,
                    },
                });
            }),
        ));

    public removeWishlistItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WishlistPublicActions.DeleteItemWishlistAction),
            concatLatestFrom(({ item }) => this.getWishlistItem(String(item.productId))),
            filter(([_, item]) => !!item),
            map(([_, item]) => {
                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.select_content,
                    eventFrom: undefined,
                    data: {
                        contentId: item.reference,
                        contentType: Ga4ContentType.wishlist,
                        imLocation: Ga4EventLocation.wishlist,
                        imType: 'wishlist',
                        imActionType: GA4ImActionType.eliminar_producto_wishlist,
                        items: [item],
                    },
                });
            }),
        ));

    private getWishlistItem(productId: string) {
        return this.store.select(WishlistPublicSelectors.getWishlistItems).pipe(
            map((items) => {
                return items.find(content => content.id === productId);
            }),
        );
    }

    public updateProductSize$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(AdjustItemSizeShopcart),
            map(({ item, size }) => {
                const addedProduct: ApiSizeInfo = {
                    ...item.colors.find((color) => color.id === item.colorId)?.sizes
                        .find((_size) => _size.sku === size.sku),
                };
                const sizeName = addedProduct.name;
                // biome-ignore lint/performance/noDelete: <explanation>
                delete addedProduct.name;

                const product = { ...item, ...addedProduct, size: sizeName } as unknown as CartItem;

                return SeleccionarTallaAnalytics({ product, config: { eventFrom: EventFrom.shopcart } });
            }),
        );
    });

    public changeSizeProduct$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SeleccionarTallaAnalytics),
            map((action) => {
                // eslint-disable-next-line max-len
                const lastItemListName = GA4ProductOriginHelpers.getProductOriginPersisting(this.coreStorageService).lastItemListName;
                const imActionType = lastItemListName?.includes('relacionados') ? GA4ImActionType.seleccionar_talla_producto_cross : GA4ImActionType.seleccionar_talla;
                const imLinkText = 'status' in action.product ? action.product.status.selectedSize?.name : action.product.size;

                return GA4AnalyticsPublicActions.Ga4Event({
                    name: GA4EventType.select_content,
                    eventFrom: action?.config?.eventFrom,
                    data: {
                        contentId: action.product.reference,
                        contentType: Ga4ContentType.product_size,
                        imActionType,
                        imLinkText,
                        items: [action.product] as ProductPublicModels.PublicProduct[],
                        relatedProductType: action?.config?.parentType,
                    },
                });
            }),
        ));

    public DispatchProductAnalytics$ = createEffect(() =>
        this.actions$.pipe(
            ofType(GA4AnalyticsActions.DispatchProductAnalyticsById),
            concatLatestFrom(() =>
                this.store.select(getProducts)),
            map(([action, products]) => {
                const product = products.find(_product => _product.id === action.productId);
                if (product?.gridElemType?.toUpperCase() === GridElemType.BUNDLE) {
                    return BundleClickAnalytics({
                        product: { ...product },
                        eventFrom: EventFrom.lookBook,
                    });
                }
                return ProductClickAnalytics({
                    product: { ...product },
                    place: undefined,
                });
            }),
        ));

    public selectPromotionOnClickCollectionStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ClickOnCollectionStoryAnalytics),
            switchMap((action) => this.actions$.pipe(
                ofType(SetPage),
                first(),
                map(() => action),
            )),
            map(({ collectionStory }) => {
                return GA4AnalyticsActions.Ga4Event({
                    name: GA4EventType.select_promotion,
                    eventFrom: EventFrom.stories,
                    data: {
                        collectionStory,
                    },
                });
            }),
        );
    });
}

// #endregion

// #region Functional Effects

export const modalOrSidebarOpenSetPageviewSent$ = createEffect((
    actions$ = inject(Actions),
) => actions$.pipe(
    ofType(OpenModalFromComponent, SidebarActionTypes.OpenSidebarCustomSuccess),
    filter(({ componentName }) => !NON_PAGEVIEW_MODALS_AND_SIDEBARS.includes(componentName)),
    map(() => Ga4SetPageviewSent({ isSent: false })),
), { functional: true });

export const modalOrSidebarCloseSetPageviewSent$ = createEffect((
    actions$ = inject(Actions),
) => actions$.pipe(
    ofType(CloseModalFromComponent, SidebarActionTypes.CloseSidebarCustomSuccess),
    filter((action) => {
        return !(
            (action as CloseSidebarCustomSuccessCommandProps)?.isNavigation
            || NON_PAGEVIEW_MODALS_AND_SIDEBARS.includes(action.componentName)
        );
    }),
    map(() => Ga4SetPageviewSent({ isSent: false })),
), { functional: true });

export const modalOrSidebarClosePageView$ = createEffect((
    actions$ = inject(Actions),
) => actions$.pipe(
    ofType(CloseModalFromComponent, SidebarActionTypes.CloseSidebarCustomSuccess),
    filter((action) =>
        !((action as CloseSidebarCustomSuccessCommandProps)?.isNavigation
            || NON_PAGEVIEW_MODALS_AND_SIDEBARS.includes(action?.componentName))),
    map(() => GA4AnalyticsActions.Ga4Event({ name: GA4EventType.page_view })),
), { functional: true });

export const ga4PageviewSendSetter$ = createEffect((
    actions$ = inject(Actions),
) => actions$.pipe(
    ofType(GA4AnalyticsActions.Ga4SendDatalayer),
    filter(action => action.dataLayer.event === GA4EventType.page_view),
    map(() => Ga4SetPageviewSent({ isSent: true })),
), { functional: true });

export const popUpViewAnalytics$ = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(GetContextOk, OpenModalFromComponent, SidebarActionTypes.OpenSidebarCustomSuccess),
        concatLatestFrom(() => [
            store.select(getActiveModalData),
            store.select(SidebarSelectors.getSidebarLoadedComponent),
        ]),
        distinctUntilChanged(),
        filter(([_, activeModalData, sidebarLoadedComponent]) =>
            isValidModal(activeModalData)
            || isValidSidebarComponent(sidebarLoadedComponent)),
        map(([_, activeModalData, sidebarLoadedComponent]) => {
            return GA4AnalyticsActions.Ga4Event({
                name: GA4EventType.page_view,
                eventFrom: EventFrom[activeModalData?.name || sidebarLoadedComponent],
            });
        }),
    );
}, { functional: true });

export const SmartAssistantSelectContentEvent$ = createEffect((
    actions$ = inject(Actions),
    commonWindow = inject(WINDOW),
) => actions$.pipe(
    ofType(ObservedElementClick),
    filter(({ observedData }) => [
        AnalyticsClickSourceID.orderStatusBanner,
        AnalyticsClickSourceID.smartAssistantLauncher,
    ].includes(observedData.clickId)),
    map(({ elementReference }) => commonWindow.document.querySelector(`[data-reference="${elementReference}"]`)?.textContent?.trim()),
    switchMap((elementTextContent) => actions$.pipe(
        ofType(SmartAssistantView),
        map(({ orderType }) => {
            const imActionType = {
                order: GA4ImActionType.order_status_view,
                'return-request': GA4ImActionType.return_status_view,
            }[orderType];

            return GA4AnalyticsPublicActions.Ga4Event({
                name: GA4EventType.select_content,
                eventFrom: EventFrom.smartAssistant,
                data: {
                    contentType: Ga4ContentType.smart_assistant,
                    imActionType,
                    imLinkText: elementTextContent,
                },
            });
        }),
    )),
), { functional: true });

/* La migración a efectos funcionales debería hacerse paulatinamente, validando después de cada
    refactor que los efectos no tiene comportamientos inesperados debido al cambio. */
export const GA4AnalyticsFunctionalEffects = {
    ga4PageviewSendSetter$,
    modalOrSidebarClosePageView$,
    modalOrSidebarCloseSetPageviewSent$,
    modalOrSidebarOpenSetPageviewSent$,
    popUpViewAnalytics$,
    SmartAssistantSelectContentEvent$,
};

// #endregion
